import {
  useLayoutEffect,
  useRef,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode
} from "react";
import { BOARD_SIZE, type Board } from "@word-duel/shared";
import {
  boardIndex,
  placementAt,
  premiumLabel,
  tileDisplayLetter,
  tileDisplayName,
  type PendingPlacement
} from "../lib/game";
import { Button } from "./ui";

export function GameBoard({
  board,
  placements,
  selectedTileId,
  canPlace,
  zoom,
  centerKey,
  dragTarget,
  draggingTileId,
  bottomDock,
  onZoom,
  onSquare,
  onPendingTilePointerDown
}: {
  board: Board;
  placements: PendingPlacement[];
  selectedTileId: string | null;
  canPlace: boolean;
  zoom: number;
  centerKey: string;
  dragTarget: { row: number; col: number; valid: boolean } | null;
  draggingTileId: string | null;
  bottomDock?: ReactNode;
  onZoom: (zoom: number) => void;
  onSquare: (row: number, col: number) => void;
  onPendingTilePointerDown: (
    event: ReactPointerEvent<HTMLButtonElement>,
    placement: PendingPlacement
  ) => void;
}) {
  const viewportRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      const viewport = viewportRef.current;
      if (!viewport) return;
      viewport.scrollLeft = Math.max(0, (viewport.scrollWidth - viewport.clientWidth) / 2);
      viewport.scrollTop = Math.max(0, (viewport.scrollHeight - viewport.clientHeight) / 2);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [centerKey, zoom]);

  return (
    <section className="board-section" aria-labelledby="board-title">
      <div className="board-toolbar">
        <div>
          <p className="eyebrow">BOARD / 15 × 15</p>
          <h2 id="board-title" className="sr-only">
            Game board
          </h2>
        </div>
        <div className="zoom-controls" aria-label="Board zoom">
          <Button
            variant="quiet"
            onClick={() => onZoom(Math.max(0.64, zoom - 0.12))}
            aria-label="Zoom board out"
          >
            −
          </Button>
          <output aria-live="polite">{Math.round(zoom * 100)}%</output>
          <Button
            variant="quiet"
            onClick={() => onZoom(Math.min(1.12, zoom + 0.12))}
            aria-label="Zoom board in"
          >
            +
          </Button>
        </div>
      </div>
      <div className={`board-stage ${bottomDock ? "board-stage--with-dock" : ""}`}>
        <div
          ref={viewportRef}
          className="board-viewport"
          data-testid="board"
          data-board-viewport="true"
          role="region"
          tabIndex={0}
          aria-labelledby="board-title"
          style={{ "--board-zoom": zoom } as CSSProperties}
        >
          <div className="game-board" role="grid" aria-label="15 by 15 word tile board">
            {Array.from({ length: BOARD_SIZE * BOARD_SIZE }, (_, index) => {
              const row = Math.floor(index / BOARD_SIZE);
              const col = index % BOARD_SIZE;
              const committed = board[boardIndex(row, col)] ?? null;
              const pending = placementAt(placements, row, col);
              const premium = premiumLabel(row, col);
              const tile = pending?.tile ?? committed;
              const letter = tile ? tileDisplayLetter(tile, pending?.blankLetter) : null;
              const tileName = tile ? tileDisplayName(tile, pending?.blankLetter) : null;
              const value = tile?.value ?? null;
              const isDropTarget = dragTarget?.row === row && dragTarget.col === col;
              const isDraggingSource = pending?.tile.id === draggingTileId;
              const kind = pending
                ? "pending"
                : committed
                  ? "committed"
                  : premium
                    ? `premium-${premium.toLowerCase()}`
                    : "empty";
              const description = pending
                ? `${tileName}, ${value} points, unsubmitted at row ${row + 1}, column ${col + 1}. Drag to move it, or tap to return it to your rack.`
                : committed
                  ? `${tileName}, ${value} points at row ${row + 1}, column ${col + 1}`
                  : `${premium ? `${premium} premium` : "Empty square"} at row ${row + 1}, column ${col + 1}${
                      selectedTileId && canPlace
                        ? ". Tap to place the selected tile, or drop a tile here."
                        : canPlace
                          ? ". Drop a tile here."
                          : ""
                    }`;
              return (
                <button
                  key={index}
                  className={`board-cell board-cell--${kind} ${committed ? `board-cell--owner-${committed.owner}` : ""} ${isDropTarget ? (dragTarget.valid ? "is-drop-target" : "is-invalid-drop-target") : ""} ${isDraggingSource ? "is-dragging-source" : ""}`}
                  role="gridcell"
                  aria-rowindex={row + 1}
                  aria-colindex={col + 1}
                  aria-label={description}
                  onClick={() => onSquare(row, col)}
                  onPointerDown={(event) => {
                    if (pending) onPendingTilePointerDown(event, pending);
                  }}
                  disabled={Boolean(committed) || (!pending && (!canPlace || !selectedTileId))}
                  data-testid={`board-cell-${row}-${col}`}
                  data-board-row={row}
                  data-board-col={col}
                  data-drop-valid={isDropTarget ? String(dragTarget.valid) : undefined}
                >
                  {tile ? (
                    <span className="board-tile">
                      <b>{letter}</b>
                      <small>{value}</small>
                      {pending && <i aria-hidden="true">+</i>}
                    </span>
                  ) : premium ? (
                    <span className="premium-label">{premium}</span>
                  ) : row === 7 && col === 7 ? (
                    <span className="center-mark" aria-hidden="true">
                      ◆
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
        {bottomDock && <div className="board-bottom-dock">{bottomDock}</div>}
      </div>
      <p className="board-hint">
        {canPlace
          ? "Drag a tile onto the board, or tap a tile then a square. Drag raised tiles to move them."
          : "Board is read-only until it is your turn."}
      </p>
    </section>
  );
}
